'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { useMutation } from '@tanstack/react-query';
import { LogOutIcon, LockIcon, MapPinIcon, PackageIcon, ShoppingCartIcon, UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PopoverClose } from '@radix-ui/react-popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { LoadingIndicator, LoginDialog, LoginForm, UserAvatar } from '@/components/partials';
import { useCartStore, useLogoutStore, useMeStore } from '@/store';
import { useTranslatedRoute } from '@/hooks';
import { getErrorMessage } from '@/lib/utils';
const AuthDropdown = ({
  hasToken
}: {
  hasToken: boolean;
}) => {
  const t = useTranslations();
  const router = useRouter();
  const {
    buildRoute
  } = useTranslatedRoute();
  const {
    cartState,
    getCart
  } = useCartStore();
  const profileRoute = buildRoute('/profile');
  const cartRoute = buildRoute('/cart');
  const cartQuantity = cartState.response?.totalQuantity ?? 0;
  const items = [{
    label: t('profile'),
    href: profileRoute,
    icon: UserIcon
  }, {
    label: t('addresses'),
    href: buildRoute('/addresses'),
    icon: MapPinIcon
  }, {
    label: t('shopping_cart'),
    href: cartRoute,
    icon: ShoppingCartIcon,
    count: cartQuantity
  }, {
    label: t('orders'),
    href: buildRoute('/orders'),
    icon: PackageIcon
  }];
  const {
    meState,
    reset
  } = useMeStore();
  const {
    response: userData,
    isLoading,
    error
  } = meState;

  // Track whether a loading cycle has ever started (covers post-logout reset case)
  const hasTriedLoadingRef = useRef(false);
  useEffect(() => {
    if (isLoading) {
      hasTriedLoadingRef.current = true;
    }
  }, [isLoading]);
  useEffect(() => {
    if (userData !== null && !cartState.response && !cartState.isLoading) {
      getCart().catch(() => {});
    }
  }, [userData, cartState.response, cartState.isLoading, getCart]);

  // Show skeleton only during the initial load when token exists but data hasn't arrived yet.
  // Once loading has been attempted (hasTriedLoadingRef), don't show skeleton again (e.g., after logout reset).
  const isPending = hasToken && !hasTriedLoadingRef.current && userData === null && !isLoading && !error;
  const {
    mutate: logoutMutate,
    isPending: isLoggingOut
  } = useMutation({
    mutationFn: async () => {
      const logoutStore = useLogoutStore.getState();
      return await logoutStore.logout();
    },
    onSuccess: data => {
      reset();
      toast.success(data.message);
      router.refresh();
    },
    onError: (error_0: any) => {
      toast.error(getErrorMessage(error_0, t('something_went_wrong')));
    }
  });
  const initials = userData !== null ? [userData.firstname?.[0], userData.lastname?.[0]].filter(Boolean).join('.') + '.' : null;
  const showSkeleton = isLoading || isPending;
  return <>
      <Popover modal={false}>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button variant="ghost" className="hover:bg-brand-darken data-[state=open]:bg-brand-darken h-7! w-7! shrink-0 flex-col items-center p-0 hover:text-white focus-visible:outline-none md:h-9! md:w-9! lg:h-auto! lg:w-auto! lg:gap-0.5 lg:px-2 lg:py-1">
                {showSkeleton ? <span className="size-5 animate-pulse rounded-full bg-current opacity-30 lg:size-5" /> : userData === null ? <LockIcon className="size-4 md:size-5" /> : <UserIcon className="size-4 md:size-5" />}
                <span className="hidden w-full text-center text-[9px] leading-none font-medium uppercase opacity-80 lg:block">
                  {showSkeleton ? <span className="inline-block h-2 w-full animate-pulse rounded bg-current opacity-30" /> : initials ?? t('login')}
                </span>
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>
            <p>{t(userData === null ? 'login' : 'profile')}</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent align="end" className={`z-60! transition-all duration-300 select-none ${showSkeleton || userData !== null ? 'w-55 p-3!' : 'w-70 sm:w-80'}`}>
          {showSkeleton ? <LoadingIndicator /> : <>
              {userData === null ? <LoginForm idPrefix="top" onLoginSuccess={() => router.refresh()} /> : <div className="space-y-2">
                  <PopoverClose asChild>
                    <Link href={profileRoute} className="flex items-center gap-3">
                      <UserAvatar user={userData} size="sm" />
                      <div className="min-w-0 leading-tight">
                        <p className="truncate text-sm font-semibold">{userData.fullName}</p>
                        <p className="text-muted-foreground truncate text-xs">{userData.email}</p>
                      </div>
                    </Link>
                  </PopoverClose>

                  <Separator />

                  <ul className="space-y-1 text-sm text-gray-500 uppercase">
                    {items.map((item, key) => <li key={key}>
                        <PopoverClose asChild>
                          <Link href={item.href} className="group hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 transition hover:text-gray-800">
                            <span className="flex items-center gap-2">
                              <item.icon className="text-muted-foreground group-hover:text-foreground h-4 w-4 transition" />
                              <span>{item.label}</span>
                            </span>
                            {typeof item.count === 'number' && item.count > 0 && <span className="bg-brand inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] leading-none font-semibold text-white">
                                {item.count > 99 ? '99+' : item.count}
                              </span>}
                          </Link>
                        </PopoverClose>
                      </li>)}
                  </ul>

                  <Separator />

                  <Button variant="destructive" size="sm" onClick={() => logoutMutate()} disabled={isLoggingOut} className="w-full uppercase">
                    {isLoggingOut ? t('logging_out') : t('logout')}
                    <LogOutIcon />
                  </Button>
                </div>}
            </>}
        </PopoverContent>
      </Popover>
      {userData === null && <LoginDialog />}
    </>;
};
export default AuthDropdown;